// src/components/ui/NuevoUsuario.jsx
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/* ===== BASE robusta: localhost o dominio ===== */
const resolveApiBase = () => {
  const env = (process.env.REACT_APP_API_BASE || '').trim();
  if (env) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  return (isLocal ? 'http://localhost:5001' : window.location.origin).replace(/\/+$/, '');
};
const API_BASE   = resolveApiBase();
const LIST_ROUTE = '/admin/usuarios';

export default function NuevoUsuario() {
  const navigate = useNavigate();

  // Form state (sin Yup)
  const [form, setForm] = useState({
    nombreCompleto: '',
    empresa: '',
    direccion: '',
    whatsapp: '',
    nit: '',
    email: '',
    tipoUsuario: '',
    password: '',
    confirmarPassword: '',
    estado: 'activo',
  });

  const [errores, setErrores] = useState({});
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [fotoFile, setFotoFile] = useState(null);
  const [preview, setPreview] = useState('');

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrores((err) => ({ ...err, [name]: '' }));
  };

  // Validación manual sencilla
  const validar = () => {
    const e = {};
    if (!form.nombreCompleto.trim()) e.nombreCompleto = 'El nombre completo es obligatorio';
    if (!form.empresa.trim())       e.empresa = 'La empresa es obligatoria';
    if (!form.direccion.trim())     e.direccion = 'La dirección es obligatoria';
    if (!form.email.trim())         e.email = 'El correo es obligatorio';
    else if (!/.+@.+\..+/.test(form.email)) e.email = 'Correo no válido';
    if (!form.tipoUsuario)          e.tipoUsuario = 'Selecciona un tipo de usuario';
    if (!form.password)             e.password = 'La contraseña es obligatoria';
    else if (form.password.length < 6) e.password = 'Mínimo 6 caracteres';
    if (!form.confirmarPassword)    e.confirmarPassword = 'Confirma tu contraseña';
    else if (form.confirmarPassword !== form.password) e.confirmarPassword = 'Las contraseñas no coinciden';
    return e;
  };

  // Avatar (iniciales + color desde el nombre)
  const avatarBg = useMemo(() => {
    const seed = (form.nombreCompleto || '').toLowerCase();
    let h = 0; for (let i=0;i<seed.length;i++) h = (h*31 + seed.charCodeAt(i)) % 360;
    return `hsl(${h} 70% 85%)`;
  }, [form.nombreCompleto]);

  const initials = useMemo(() => {
    const parts = form.nombreCompleto.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last  = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || 'U';
  }, [form.nombreCompleto]);

  const onFoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type?.includes('image')) return alert('Solo imágenes');
    setFotoFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const eVal = validar();
    setErrores(eVal);
    if (Object.keys(eVal).length) return;

    try {
      setSubiendo(true);
      setProgreso(0);

      const fd = new FormData();
      fd.append('nombreCompleto', form.nombreCompleto.trim());
      fd.append('empresa', form.empresa.trim());
      fd.append('direccion', form.direccion.trim());
      fd.append('whatsapp', form.whatsapp.trim());
      fd.append('nit', form.nit.trim());
      fd.append('email', form.email.trim());
      fd.append('tipoUsuario', form.tipoUsuario); // clave para evitar NULL en DB
      fd.append('password', form.password);
      fd.append('estado', form.estado);
      if (fotoFile) fd.append('foto', fotoFile);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/api/admin/usuarios`, true); // << RUTA CORRECTA (adminUsuarios)
      xhr.withCredentials = true;
      xhr.responseType = 'json';

      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) setProgreso(Math.round((ev.loaded / ev.total) * 100));
      };

      xhr.timeout = 20000;          // defensivo por antivirus/proxy
      xhr.ontimeout = () => {       // UX consistente en timeout
        setSubiendo(false);
        alert('timeout');
      };

      const resp = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response ?? JSON.parse(xhr.responseText || '{}'));
          } else {
            const msg = xhr.response?.msg || `Error ${xhr.status}`;
            reject(new Error(msg));
          }
        };
        xhr.onerror = () => reject(new Error('Error de red'));
        xhr.send(fd);
      });

      if (resp?.ok === false) throw new Error(resp?.msg || 'No se pudo crear');
      setProgreso(100);
      setSubiendo(false);
      alert('✅ Usuario creado correctamente');
      navigate(LIST_ROUTE, { replace: true });
    } catch (err) {
      setSubiendo(false);
      console.error(err);
      alert(String(err.message || err));
    } finally {
      // Limpiar progreso inmediatamente, sin demoras cosméticas
      setProgreso(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800">Nuevo usuario</h1>
          <p className="text-slate-500">Usuarios del panel administrativo.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate(LIST_ROUTE)}
          className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700"
          disabled={subiendo}
        >
          ← Volver
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow p-6 sm:p-8">
        <form onSubmit={onSubmit} className="grid md:grid-cols-5 gap-6" autoComplete="off">
          {/* Avatar */}
          <div className="md:col-span-2">
            <div className="rounded-2xl border p-3">
              <div className="aspect-square rounded-xl overflow-hidden border bg-white flex items-center justify-center">
                {preview ? (
                  <img src={preview} alt="Foto" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center" style={{ background: avatarBg }}>
                    <span className="text-4xl font-bold text-slate-700">{initials}</span>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <label
                  htmlFor="foto"
                  className={`inline-flex items-center px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 cursor-pointer text-sm ${subiendo ? 'opacity-60 pointer-events-none' : ''}`}
                >
                  Seleccionar foto
                  <input id="foto" type="file" accept="image/*" onChange={onFoto} className="hidden" disabled={subiendo} />
                </label>
                <span className="text-xs text-slate-500 truncate">
                  {fotoFile?.name || 'Opcional'}
                </span>
              </div>

              {progreso !== null && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-slate-600">{progreso === 100 ? 'Completado' : 'Subiendo…'}</span>
                    <span className="text-slate-500">{progreso}%</span>
                  </div>
                  <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-200 ${progreso === 100 ? 'bg-emerald-500' : 'bg-sky-500'}`}
                      style={{ width: `${progreso}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Formulario */}
          <div className="md:col-span-3">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Nombre completo"
                name="nombreCompleto"
                value={form.nombreCompleto}
                onChange={onChange}
                error={errores.nombreCompleto}
                disabled={subiendo}
                full
              />
              <Field label="Empresa" name="empresa" value={form.empresa} onChange={onChange} error={errores.empresa} disabled={subiendo} full />
              <Field label="Dirección" name="direccion" value={form.direccion} onChange={onChange} error={errores.direccion} disabled={subiendo} full />
              <Field label="WhatsApp" name="whatsapp" value={form.whatsapp} onChange={onChange} error={errores.whatsapp} disabled={subiendo} />
              <Field label="NIT" name="nit" value={form.nit} onChange={onChange} error={errores.nit} disabled={subiendo} />
              <Field type="email" label="Email" name="email" value={form.email} onChange={onChange} error={errores.email} disabled={subiendo} />

              {/* Tipo de usuario */}
              <div>
                <label className="block text-sm font-medium text-slate-700">Tipo de usuario</label>
                <select
                  name="tipoUsuario"
                  value={form.tipoUsuario}
                  onChange={onChange}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 bg-white ${errores.tipoUsuario ? 'border-rose-400' : 'border-slate-300'}`}
                  disabled={subiendo}
                >
                  <option value="">Seleccione una opción</option>
                  <option value="adminGeneral">Administrador General</option>
                  <option value="adminTec">Administrador Técnico</option>
                  <option value="adminComercial">Administrador Comercial</option>
                  <option value="bodeguero">Bodeguero</option>
                  <option value="servicioCliente">Servicio al Cliente</option>
                  <option value="despacho">Despacho de pedidos</option>
                  <option value="vendedor">Vendedor</option>
                  <option value="mensajero">Mensajero</option>
                </select>
                {errores.tipoUsuario && <p className="text-rose-600 text-sm mt-1">{errores.tipoUsuario}</p>}
              </div>

              {/* Estado */}
              <div>
                <label className="block text-sm font-medium text-slate-700">Estado</label>
                <select
                  name="estado"
                  value={form.estado}
                  onChange={onChange}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                  disabled={subiendo}
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>

              {/* Passwords */}
              <Field type="password" label="Contraseña" name="password" value={form.password} onChange={onChange} error={errores.password} disabled={subiendo} />
              <Field type="password" label="Confirmar contraseña" name="confirmarPassword" value={form.confirmarPassword} onChange={onChange} error={errores.confirmarPassword} disabled={subiendo} />
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="submit"
                disabled={subiendo}
                className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60"
              >
                {subiendo ? 'Guardando…' : 'Crear usuario'}
              </button>
              <button
                type="button"
                onClick={() => navigate(LIST_ROUTE)}
                className="px-5 py-2 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold"
                disabled={subiendo}
              >
                Cancelar
              </button>
            </div>

            <p className="text-xs text-slate-500 mt-3">
              Si no adjuntas foto, el backend usará <code>nodisponible.png</code> automáticamente.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, error, type='text', disabled=false, full=false }) {
  return (
    <div className={full ? 'sm:col-span-2' : undefined}>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className={`mt-1 w-full rounded-lg border px-3 py-2 ${error ? 'border-rose-400' : 'border-slate-300'}`}
        disabled={disabled}
        autoComplete="off"
      />
      {error && <p className="text-rose-600 text-sm mt-1">{error}</p>}
    </div>
  );
}
